// src/AdminPanel/AddProduct.js
import React, { useState } from "react";
import { db } from "../firebase/firebase";
import { collection, addDoc } from "firebase/firestore";
import toast from "../utils/toast";
import { useSale } from "../contexts/SaleContext";
import CloudinaryUpload from "../components/CloudinaryUpload";
import {
  getProductBehaviorType,
  PRODUCT_BEHAVIOR,
  TYPE_OPTIONS,
  WEIGHT_QUANTITY_SUGGESTIONS,
} from "../utils/productUtils";


function AddProduct() {
  const { sale, discountLabel, categories } = useSale();

  // ── Common fields ──────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [formula, setFormula] = useState("");
  const [type, setType] = useState("");
  const [manufacturedBy, setManufacturedBy] = useState("");
  const [picture, setPicture] = useState("");
  const [description, setDescription] = useState("");
  const [usage, setUsage] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Standard / Weight fields ───────────────────────────────────────────────
  const [price, setPrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [quantityPerPack, setQuantityPerPack] = useState("");

  // ── Medicine-specific fields ───────────────────────────────────────────────
  const [stripsPerPack, setStripsPerPack] = useState("");
  const [tabletsPerStrip, setTabletsPerStrip] = useState("");
  const [packEnabled, setPackEnabled] = useState(true);
  const [packPrice, setPackPrice] = useState("");
  const [packStock, setPackStock] = useState("");
  const [stripEnabled, setStripEnabled] = useState(false);
  const [stripPrice, setStripPrice] = useState("");
  const [stripStock, setStripStock] = useState("");
  const [medSellingPrice, setMedSellingPrice] = useState("");
  const [medRetailPrice, setMedRetailPrice] = useState("");

  // ── Derived ────────────────────────────────────────────────────────────────
  const behaviorType = getProductBehaviorType(type);
  const isMedicine = behaviorType === PRODUCT_BEHAVIOR.MEDICINE;
  const isWeight = behaviorType === PRODUCT_BEHAVIOR.WEIGHT;

  const finalDiscountLabel = sale.isActive ? discountLabel || "" : "";

  // ── Validation ─────────────────────────────────────────────────────────────
  const medicineValid =
    (packEnabled ? packPrice !== "" : true) &&
    (stripEnabled ? stripPrice !== "" : true) &&
    (packEnabled || stripEnabled);

  const isFormValid =
    name.trim() !== "" &&
    type.trim() !== "" &&
    picture.trim() !== "" &&
    (isMedicine ? medicineValid : price !== "");

  // ── Reset ──────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setName(""); setFormula(""); setType(""); setManufacturedBy("");
    setPicture("");
    setDescription(""); setUsage(""); setBatchNo("");
    setPrice(""); setRetailPrice(""); setQuantityPerPack("");
    setStripsPerPack(""); setTabletsPerStrip("");
    setPackEnabled(true); setPackPrice(""); setPackStock("");
    setStripEnabled(false); setStripPrice(""); setStripStock("");
    setMedSellingPrice(""); setMedRetailPrice("");
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      const base = {
        name, formula, type, manufacturedBy, picture,
        discounts: finalDiscountLabel,
        discountMode: "auto",
        description, usage, batchNo,
        createdAt: new Date(),
      };

      let extra = {};
      if (isMedicine) {
        const pPrc = packEnabled && packPrice ? parseFloat(packPrice) : 0;
        const sPrc = stripEnabled && stripPrice ? parseFloat(stripPrice) : 0;
        extra = {
          quantityUnit: "medicine",
          packaging: {
            stripsPerPack: stripsPerPack ? parseInt(stripsPerPack, 10) : 0,
            tabletsPerStrip: tabletsPerStrip ? parseInt(tabletsPerStrip, 10) : 0,
          },
          sellingOptions: {
            pack: { enabled: packEnabled, label: "Full Pack", price: pPrc, stock: packStock ? parseInt(packStock, 10) : 0 },
            strip: { enabled: stripEnabled, label: "Strip", price: sPrc, stock: stripStock ? parseInt(stripStock, 10) : 0 },
          },
          // Selling / Retail price for card display (explicitly set by admin)
          price: medSellingPrice !== "" ? parseFloat(medSellingPrice) : (packEnabled && pPrc ? pPrc : sPrc),
          retailPrice: medRetailPrice !== "" ? parseFloat(medRetailPrice) : (packEnabled && pPrc ? pPrc : sPrc),
        };
      } else if (isWeight) {
        extra = {
          price: parseFloat(price) || 0,
          retailPrice: parseFloat(retailPrice) || 0,
          quantityPerPack: quantityPerPack || "",
          quantityUnit: "weight",
        };
      } else {
        extra = {
          price: parseFloat(price) || 0,
          retailPrice: parseFloat(retailPrice) || 0,
          quantityPerPack: quantityPerPack ? parseInt(quantityPerPack, 10) : 0,
          quantityUnit: "count",
        };
      }

      await addDoc(collection(db, "products"), { ...base, ...extra });
      toast.success("Product added successfully");
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add product");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="upload-page">
      <div className="upload-container">
        <h2>Add New Product</h2>
        <form className="upload-form" onSubmit={handleSubmit}>
          {/* ── Batch No ── */}
          <div className="form-group">
            <label htmlFor="ap-batchNo">Batch No.</label>
            <input id="ap-batchNo" type="text" value={batchNo}
              onChange={(e) => setBatchNo(e.target.value)}
              placeholder="Enter Batch Number" />
          </div>

          {/* ── Name ── */}
          <div className="form-group">
            <label htmlFor="ap-name">Product Name <span className="cu-required">*</span></label>
            <input id="ap-name" type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              list="ap-nameOpts" placeholder="Enter product name" />
          </div>

          {/* ── Formula ── */}
          <div className="form-group">
            <label htmlFor="ap-formula">Formula</label>
            <input id="ap-formula" type="text" value={formula}
              onChange={(e) => setFormula(e.target.value)}
              placeholder="e.g. Paracetamol 500mg" />
          </div>

          {/* ── Type ── */}
          <div className="form-group">
            <label htmlFor="ap-type">Category <span className="cu-required">*</span></label>
            <select
              id="ap-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="" disabled>Select a Category</option>
              {categories.map((t, i) => <option key={i} value={t}>{t}</option>)}
            </select>
          </div>

          {/* ══════════════════════════════════════════════
              MEDICINE — Packaging + Selling Options
          ══════════════════════════════════════════════ */}
          {isMedicine && (
            <>
              {/* Packaging */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="form-group">
                  <label htmlFor="ap-spp">Strips per Pack</label>
                  <input id="ap-spp" type="number" min="0" value={stripsPerPack}
                    onChange={(e) => setStripsPerPack(e.target.value)}
                    placeholder="e.g. 10" />
                </div>
                <div className="form-group">
                  <label htmlFor="ap-tps">Tablets per Strip</label>
                  <input id="ap-tps" type="number" min="0" value={tabletsPerStrip}
                    onChange={(e) => setTabletsPerStrip(e.target.value)}
                    placeholder="e.g. 10" />
                </div>
              </div>

              {/* ── Medicine Selling & Retail Price ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label htmlFor="ap-medSellingPrice">Selling Price (Rs.)</label>
                  <input id="ap-medSellingPrice" type="number" min="0" value={medSellingPrice}
                    onChange={(e) => setMedSellingPrice(e.target.value)}
                    placeholder="e.g. 950" />
                  <small style={{ color: "#888", marginTop: 3, display: "block" }}>Price shown to customer (discounted)</small>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label htmlFor="ap-medRetailPrice">Retail Price (Rs.)</label>
                  <input id="ap-medRetailPrice" type="number" min="0" value={medRetailPrice}
                    onChange={(e) => setMedRetailPrice(e.target.value)}
                    placeholder="e.g. 1200" />
                  <small style={{ color: "#888", marginTop: 3, display: "block" }}>Original / MRP price</small>
                </div>
              </div>

              {/* Selling Options */}

              {/* Pack */}
              <div className="selling-option-card" style={{ border: `2px solid ${packEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: packEnabled ? "#fff8f8" : "#fafafa" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: packEnabled ? 10 : 0 }}>
                  <input type="checkbox" checked={packEnabled} onChange={(e) => setPackEnabled(e.target.checked)} />
                  Sell as Full Pack
                </label>
                {packEnabled && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="ap-packPrice">Pack Price (Rs.) *</label>
                      <input id="ap-packPrice" type="number" min="0" value={packPrice}
                        onChange={(e) => setPackPrice(e.target.value)} placeholder="e.g. 1200" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="ap-packStock">Pack Stock</label>
                      <input id="ap-packStock" type="number" min="0" value={packStock}
                        onChange={(e) => setPackStock(e.target.value)} placeholder="e.g. 20" />
                    </div>
                  </div>
                )}
              </div>

              {/* Strip */}
              <div className="selling-option-card" style={{ border: `2px solid ${stripEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: stripEnabled ? "#fff8f8" : "#fafafa" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: stripEnabled ? 10 : 0 }}>
                  <input type="checkbox" checked={stripEnabled} onChange={(e) => setStripEnabled(e.target.checked)} />
                  Sell as Strip
                </label>
                {stripEnabled && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="ap-stripPrice">Strip Price (Rs.) *</label>
                      <input id="ap-stripPrice" type="number" min="0" value={stripPrice}
                        onChange={(e) => setStripPrice(e.target.value)} placeholder="e.g. 140" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="ap-stripStock">Strip Stock</label>
                      <input id="ap-stripStock" type="number" min="0" value={stripStock}
                        onChange={(e) => setStripStock(e.target.value)} placeholder="e.g. 80" />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════
              NON-MEDICINE — Standard price / quantity
          ══════════════════════════════════════════════ */}
          {!isMedicine && (
            <>
              <div className="form-group">
                <label htmlFor="ap-price">Price (Selling Price) <span className="cu-required">*</span></label>
                <input id="ap-price" type="number" value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter selling price" min="0" />
              </div>
              <div className="form-group">
                <label htmlFor="ap-retailPrice">Retail Price (Original Price)</label>
                <input id="ap-retailPrice" type="number" value={retailPrice}
                  onChange={(e) => setRetailPrice(e.target.value)}
                  placeholder="Enter original/retail price" min="0" />
              </div>
              <div className="form-group">
                <label htmlFor="ap-qty">
                  {isWeight ? "Pack Size / Weight" : "Size"}
                  {isWeight && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: "#1565c0", color: "#fff", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>
                      Weight / Volume
                    </span>
                  )}
                </label>
                <input id="ap-qty" type="text" value={quantityPerPack}
                  onChange={(e) => setQuantityPerPack(e.target.value)}
                  list="ap-qtyOpts"
                  placeholder={isWeight ? "e.g. 200ml, 500ml, 1kg, 2 Liter" : "e.g. 10, 20, 100"} />
                <datalist id="ap-qtyOpts">
                  {(WEIGHT_QUANTITY_SUGGESTIONS[type] || []).map((s, i) => <option key={i} value={s} />)}
                </datalist>
                {isWeight && (
                  <small style={{ color: "#1565c0", marginTop: 4, display: "block" }}>
                    Enter the pack size or weight (e.g. <strong>1kg</strong>, <strong>500ml</strong>).
                  </small>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="ap-mfg">Manufactured By</label>
            <input id="ap-mfg" type="text" value={manufacturedBy}
              onChange={(e) => setManufacturedBy(e.target.value)}
              placeholder="Manufacturer / brand name" />
          </div>

          {/* ── Image ── */}
          <CloudinaryUpload id="ap-picture" label="Product Image" required value={picture} onChange={setPicture} />

          {/* ── Auto Discount ── */}
          <div className="form-group">
            <label htmlFor="ap-discounts">
              Discount Badge
              {sale.isActive && <span style={{ marginLeft: 8, fontSize: 11, background: "#d2222d", color: "#fff", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>Auto</span>}
            </label>
            <input id="ap-discounts" type="text"
              value={sale.isActive ? discountLabel || "" : ""}
              readOnly disabled
              placeholder={sale.isActive ? discountLabel || "No sale active" : "No active sale — go to Manage Sale"}
              style={{ background: "#f5f5f5", color: "#888", cursor: "not-allowed" }} />
            {sale.isActive
              ? <small style={{ color: "#d2222d", marginTop: 4, display: "block" }}>Discount set globally via <strong>Manage Sale</strong>.</small>
              : <small style={{ color: "#aaa", marginTop: 4, display: "block" }}>Enable a sale from <strong>Manage Sale</strong> to auto-fill.</small>}
          </div>

          {/* ── Description ── */}
          <div className="form-group">
            <label htmlFor="ap-desc">Description</label>
            <input id="ap-desc" type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description about the medicine" />
          </div>

          {/* ── Usage ── */}
          <div className="form-group">
            <label htmlFor="ap-usage">Usage Instructions</label>
            <input id="ap-usage" type="text" value={usage}
              onChange={(e) => setUsage(e.target.value)}
              placeholder="How to use this medicine" />
          </div>

          {/* ── Submit ── */}
          <div className="form-submit-row add-product-btn-outer-div">
            <button type="submit" disabled={loading || !isFormValid} className="add-product-btn">
              {loading
                ? <><i className="fa-solid fa-spinner fa-spin" /> Adding...</>
                : <><i className="fa-solid fa-plus" /> Add Product</>}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default AddProduct;
